import Config from 'react-native-config';
import { Thunk } from 'store/types';
import dayjs from 'dayjs';
import { getErrorCode, getErrorMessage } from 'services/api/utils';
import {
  AdapterSource,
  DispatcherAuthPrefix,
  DispatcherCategoryService,
  DispatcherResource,
  DispatcherEndpoint
} from 'modules/types/common';
import { getDispatcherUrl } from 'modules/utils/UrlUtils';
import { DFDHeaders } from 'serverTypes/authentication';
import { apiKeySelector, encryptionSettingsSelector } from 'store/appSettings/selectors';
import { languageSelector } from 'store/consumerPreferences/selectors';
import Encryption from 'services/Encryption';
import { CovidScreeningPatientInfoSelector } from './selectors';
import {
  KEY,
  Patient,
  Questionnaire,
  Subscriber,
  InsuranceCard,
  CovidScreeningState,
  OrderStatus,
  FormValues
} from './types';

export const COVID_SCREENING_CONFIRM = 'COVID_SCREENING_CONFIRM';
export const COVID_SCREENING_HAS_INSURANCE = 'COVID_SCREENING_HAS_INSURANCE';
export const SET_PATIENT = `${KEY}.SET_PATIENT`;
export const SET_PREV_PATIENT = 'SET_PREV_PATIENT';
export const SET_QUESTIONNAIRE = `${KEY}.SET_QUESTIONNAIRE `;
export const SET_INSURANCE_CARD = `${KEY}.SET_INSURANCE_CARD `;
export const SET_SUBSCRIBER = `${KEY}.SET_SUBSCRIBER `;
export const RESET_COVID_SCREENING = 'RESET_COVID_SCREENING';
export const GET_LIST_INSURANCE = 'GET_LIST_INSURANCE';
export const GET_LIST_INSURANCE_SUCCESS = 'GET_LIST_INSURANCE_SUCCESS';
export const GET_LIST_INSURANCE_FAIL = 'GET_LIST_INSURANCE_FAIL';
export const SET_ORDER_STATUS = 'SET_ORDER_STATUS';
export const SET_OTP_VALIDATION = `${KEY}.SET_OTP_VALIDATION`;

export const ADD_INSURANCE_CARD = `${KEY}.ADD_INSURANCE_CARD`;
export const ADD_INSURANCE_CARD_SUCCESS = `${KEY}.ADD_INSURANCE_CARD_SUCCESS`;
export const ADD_INSURANCE_CARD_FAIL = `${KEY}.ADD_INSURANCE_CARD_FAIL`;

export const SEND_SCREENING_DATA = `${KEY}.SEND_SCREENING_DATA`;
export const SEND_SCREENING_DATA_SUCCESS = `${KEY}.SEND_SCREENING_DATA_SUCCESS`;
export const SEND_SCREENING_DATA_FAIL = `${KEY}.SEND_SCREENING_DATA_FAIL`;

export const SEND_PIN_OTP = 'COVID.SEND_PIN_OTP';
export const VERIFY_OTP = 'COVID.VERIFY_OTP';

export const setPatient = (patient: Patient) => ({
  type: SET_PATIENT,
  payload: patient
});

export const setPrevPatient = (patient: Patient) => ({
  type: SET_PREV_PATIENT,
  payload: patient
});

export const setQuestionnaire = (questionnaire: Questionnaire) => ({
  type: SET_QUESTIONNAIRE,
  payload: questionnaire
});

export const setInsuranceCard = (insuranceCard: InsuranceCard[] | null) => ({
  type: SET_INSURANCE_CARD,
  payload: insuranceCard
});
export const setSubscriber = (subscriber: Subscriber) => ({
  type: SET_SUBSCRIBER,
  payload: subscriber
});
export const resetCovidScreening = () => ({
  type: RESET_COVID_SCREENING
});

export const covidOrderStatus = (data: OrderStatus) => ({
  type: SET_ORDER_STATUS,
  payload: data
});

export const setOTPValidation = (isValidated: Boolean) => ({
  type: SET_OTP_VALIDATION,
  payload: isValidated
});

export const createConsumerObject = (values: FormValues) => {
  return {
    registrationStatus: 'AccountReverification',
    firstName: values?.userFirstName,
    lastName: values?.userLastName,
    middleName: null,
    dateOfBirth: dayjs(values?.userDateOfBirth).format('MM/DD/YYYY'),
    emails: [{ primary: true, value: values?.userEmail, verified: false, type: 'HOME' }],
    phones: [
      {
        primary: true,
        type: 'MOBILE',
        value: values?.userHasNoCellNumber
          ? values?.userOtherPhoneNumber.replace(/[^a-zA-Z0-9]/g, '')
          : values?.userCellNumber.replace(/[^a-zA-Z0-9]/g, ''),
        verified: false
      }
    ],
    addresses: [
      {
        streetAddress: values?.userAddressLine1,
        streetAddress2: null,
        city: values?.userCity,
        state: values?.userState,
        postalCode: values?.userZip,
        country: 'US',
        type: 'HOME',
        primary: true
      }
    ],
    gender:
      values?.userSex === 'BOTH'
        ? 'OTHER'
        : values?.userSex === 'UNKNOWN'
        ? 'PREFER NOT TO RESPOND'
        : values?.userSex
  };
};

export const addInsuranceCard = (
  file?: File,
  uri?: string,
  name?: string,
  type?: string
): Thunk => async dispatch => {
  // eslint-disable-next-line no-undef
  const formData = new FormData();

  // If file then it's web version
  if (file) {
    formData.append('file', file);
  } else {
    const fileToUpload = { uri, type, name };
    formData.append('file', fileToUpload);
  }

  const result = await dispatch({
    type: ADD_INSURANCE_CARD,
    payload: Config.SYMPTOM_CHECKER_COVID19_EXPANSION_ENABLED
      ? {
          client: 'dispatcher',
          request: {
            method: 'post',
            headers: {
              [DFDHeaders.mhpAdapter]: AdapterSource.MHP,
              [DFDHeaders.contentType]: 'multipart/form-data'
            },
            url: getDispatcherUrl(
              DispatcherCategoryService.DELIVER_CARE,
              DispatcherAuthPrefix.AUTHENTICATED,
              DispatcherResource.INSURANCES,
              DispatcherEndpoint.UPLOAD
            ),
            data: formData
          }
        }
      : {
          client: 'symptomChecker',
          request: {
            method: 'post',
            headers: { 'Content-Type': 'multipart/form-data' },
            url: '/covid/upload',
            data: formData
          }
        }
  });

  if (result.error) {
    if (Config.SYMPTOM_CHECKER_COVID19_EXPANSION_ENABLED) {
      return {
        error: result.error.response.data.errors[0],
        errorCode: result.error.response.data.errors[0].errorCode
      };
    }
    return {
      error: getErrorMessage(result.error),
      errorCode: getErrorCode(result.error)
    };
  }

  return {
    azureFileName: result.payload?.data?.azureFileName,
    actionStatus: result.payload?.data?.actionStatus
  };
};

export const getInsuranceList = () => ({
  type: GET_LIST_INSURANCE,
  payload: Config.SYMPTOM_CHECKER_COVID19_EXPANSION_ENABLED
    ? {
        client: 'dispatcher',
        method: 'GET',
        request: {
          url: getDispatcherUrl(
            DispatcherCategoryService.DELIVER_CARE,
            DispatcherAuthPrefix.PUBLIC,
            DispatcherEndpoint.HEALTH_PLAN_LIST
          ),
          headers: {
            [DFDHeaders.mhpAdapter]: AdapterSource.MHP
          }
        }
      }
    : {
        client: 'symptomChecker',
        request: {
          url: `/covid/healthPlanList`,
          method: 'GET'
        }
      }
});

export const sendOTPAuthenticated = (values: FormValues): Thunk => async (dispatch, getState) => {
  const apikey = apiKeySelector(getState());

  const data = {
    consumer: createConsumerObject(values),
    consumerVerification: {
      sendOTP: true,
      verifyUser: false,
      verificationStatus: 'SendOTP',
      otpType: 'sms'
    }
  };

  const response = await dispatch({
    type: SEND_PIN_OTP,
    payload: {
      client: 'profile',
      request: {
        method: 'post',
        headers: { apikey },
        url: '/iddataweb/sendotpcode',
        data
      }
    }
  });
  return response;
};

export const verifyOTPAuthenticated = (values: FormValues, pin: string): Thunk => async (
  dispatch,
  getState
) => {
  const apikey = apiKeySelector(getState());

  const data = {
    consumer: createConsumerObject(values),
    consumerVerification: {
      sendOTP: false,
      verifyUser: false,
      verificationStatus: 'ValidateOTP',
      otpType: 'sms',
      validateOTP: true,
      pin
    }
  };

  const response = await dispatch({
    type: VERIFY_OTP,
    payload: {
      client: 'profile',
      request: {
        method: 'post',
        headers: { apikey },
        url: '/iddataweb/validateotpcode',
        data
      }
    }
  });
  return response;
};

// Unauthenticated dispatcher

export const addInsuranceCardUnauthenticated = (file?: File): Thunk => async (
  dispatch,
  getState
) => {
  // eslint-disable-next-line no-undef
  const formData = new FormData();
  formData.append('file', file);

  const state = getState();
  const { names, phones } = CovidScreeningPatientInfoSelector(state);
  const encryptionSettings = encryptionSettingsSelector(state);

  const { givenName, familyName } = names?.[0];
  const phoneNumber = phones?.[0].phoneNumber
    ? phones?.[0].areaCode + phones?.[0].phoneNumber
    : phones?.[1].areaCode + phones?.[1].phoneNumber;

  const encryptedData = Encryption.encrypt(
    encryptionSettings?.algorithm,
    encryptionSettings?.password,
    `${givenName}:${familyName}:${phoneNumber}`
  );

  const result = await dispatch({
    type: ADD_INSURANCE_CARD,
    payload: {
      client: 'dispatcher',
      request: {
        method: 'post',
        headers: {
          [DFDHeaders.contentType]: 'multipart/form-data',
          [DFDHeaders.mhpAdapter]: AdapterSource.MHP,
          [DFDHeaders.mhpGenPatientId]: encryptedData
        },
        url: getDispatcherUrl(
          DispatcherCategoryService.DELIVER_CARE,
          DispatcherAuthPrefix.PUBLIC,
          DispatcherResource.INSURANCES,
          DispatcherEndpoint.UPLOAD
        ),
        data: formData
      }
    }
  });

  if (result.error) {
    return {
      error: result.error.response.data.errors[0],
      errorCode: result.error.response.data.errors[0].errorCode
    };
  }
  return {
    azureFileName: result.payload?.data?.azureFileName,
    actionStatus: result.payload?.data?.actionStatus
  };
};

export const sendCovidScreeningData = (data: CovidScreeningState) => ({
  type: SEND_SCREENING_DATA,
  payload: Config.SYMPTOM_CHECKER_COVID19_EXPANSION_ENABLED
    ? {
        client: 'dispatcher',
        request: {
          method: 'post',
          url: getDispatcherUrl(
            DispatcherCategoryService.DELIVER_CARE,
            DispatcherAuthPrefix.AUTHENTICATED,
            DispatcherResource.COVIDSCREENING,
            DispatcherEndpoint.ORDER
          ),
          data,
          headers: {
            [DFDHeaders.mhpAdapter]: AdapterSource.MHP,
            [DFDHeaders.contentType]: 'application/json'
          }
        }
      }
    : {
        client: 'symptomChecker',
        request: {
          url: `/covid/submitOrder`,
          method: 'post',
          data
        }
      }
});

export const sendGuestCovidScreeningData = (data: CovidScreeningState): Thunk => async (
  dispatch,
  getState
) => {
  const language = languageSelector(getState());
  const headers = {
    [DFDHeaders.mhpAdapter]: AdapterSource.MHP
  };
  if (Config.PRIMARY_LANGUAGE_SWITCHER === 'enabled') {
    headers[DFDHeaders.mhpLanguageCode] = language.toUpperCase();
  }
  dispatch({
    type: SEND_SCREENING_DATA,
    payload: {
      client: 'dispatcher',
      request: {
        method: 'post',
        url: getDispatcherUrl(
          DispatcherCategoryService.DELIVER_CARE,
          DispatcherAuthPrefix.PUBLIC,
          DispatcherResource.COVIDSCREENING,
          DispatcherEndpoint.ORDER
        ),
        data,
        headers
      }
    }
  });
};

export const sendOTP = (values: FormValues, currentLanguage: string): Thunk => async (
  dispatch,
  getState
) => {
  const apikey = apiKeySelector(getState());
  const otpType = values.userHasNoCellNumber ? 'email' : 'sms';

  const data = {
    consumer: createConsumerObject(values),
    consumerVerification: {
      sendOTP: true,
      verifyUser: false,
      verificationStatus: 'SendOTP',
      otpType
    }
  };

  const response = await dispatch({
    type: SEND_PIN_OTP,
    payload: {
      client: 'profile',
      request: {
        method: 'post',
        headers: { apikey, 'mhp-language-code': currentLanguage },
        url: '/iddataweb/public/v2/sendotpcode',
        data
      }
    }
  });
  return response;
};

export const verifyOTP = (
  values: FormValues,
  pin: string,
  currentLanguage: string
): Thunk => async (dispatch, getState) => {
  const apikey = apiKeySelector(getState());
  const otpType = values.userHasNoCellNumber ? 'email' : 'sms';

  const data = {
    consumer: createConsumerObject(values),
    consumerVerification: {
      sendOTP: false,
      verifyUser: false,
      verificationStatus: 'ValidateOTP',
      otpType,
      validateOTP: true,
      pin
    }
  };

  const response = await dispatch({
    type: VERIFY_OTP,
    payload: {
      client: 'profile',
      request: {
        method: 'post',
        headers: { apikey, 'mhp-language-code': currentLanguage },
        url: '/iddataweb/public/v2/validateotpcode',
        data
      }
    }
  });
  return response;
};

export const getInsuranceListUnauthenticated = () => ({
  type: GET_LIST_INSURANCE,
  payload: {
    client: 'dispatcher',
    method: 'GET',
    request: {
      url: getDispatcherUrl(
        DispatcherCategoryService.DELIVER_CARE,
        DispatcherAuthPrefix.PUBLIC,
        DispatcherEndpoint.HEALTH_PLAN_LIST
      ),
      headers: {
        [DFDHeaders.mhpAdapter]: AdapterSource.MHP
      }
    }
  }
});
